/**
 * Knex implementation of ISingleRepository.
 *
 * Single entities are KV-style: one global record, or multiple records
 * partitioned by a `scopeField` (e.g. mail config per tenant).
 *
 * Pure Knex adapter — no service deps, no hooks, no JSON parsing.
 * Services handle hooks, JSON parsing, and business rules on top.
 */

import type { Knex } from 'knex'
import type { ISingleRepository } from '../types.js'
import { handleDbError } from '../../../db/knex-adapter.js'

export interface KnexSingleRepoOptions {
  /**
   * Field name for scoping (e.g. 'tenant', 'provider').
   * If undefined, the table contains a single global record.
   */
  scopeField?: string
  /** Generates IDs for inserts (when no record exists yet). */
  idGenerator: () => string
}

/**
 * Knex-backed implementation of ISingleRepository.
 *
 * Scoped mode (scopeField defined):
 *   Each scope value corresponds to one row in the table.
 *   `get('tenant1')` returns the row where `<scopeField> = 'tenant1'`.
 *   `get()` with no argument returns the first row (undefined scope).
 *
 * Global mode (no scopeField):
 *   The table holds exactly one row.
 *   `get()` returns that row or null.
 *   `upsert()` updates the existing row or creates one.
 */
export class KnexSingleRepository<T extends Record<string, unknown> = Record<string, unknown>>
  implements ISingleRepository<T>
{
  constructor(
    protected readonly knex: Knex,
    protected readonly table: string,
    protected readonly options: KnexSingleRepoOptions
  ) {}

  // ── get ─────────────────────────────────────────────────────────────────

  async get(scope?: string): Promise<T | null> {
    const { scopeField } = this.options

    if (scopeField && scope !== undefined) {
      const row = await this.knex(this.table).where(scopeField, scope).first<T | undefined>()
      return row ?? null
    }

    // No scope arg (either global mode or scoped-but-no-arg): return first row
    const row = await this.knex(this.table).first<T | undefined>()
    return row ?? null
  }

  // ── upsert ──────────────────────────────────────────────────────────────

  async upsert(data: Partial<T>, scope?: string): Promise<T> {
    const { scopeField, idGenerator } = this.options

    if (scopeField) {
      // Scoped mode — requires scope to be provided for a meaningful upsert
      const existing = await this.knex(this.table)
        .where(scopeField, scope)
        .first<T | undefined>()

      if (existing) {
        try {
          const updated = await this.knex(this.table)
            .where(scopeField, scope)
            .update(data)
            .returning('*')
          // SQLite does not support RETURNING — fall back to re-reading
          return (updated[0] ?? (await this.get(scope)) ?? { ...existing, ...data }) as T
        } catch (e) {
          handleDbError(e)
        }
      } else {
        try {
          const inserted = await this.knex(this.table)
            .insert({ id: idGenerator(), [scopeField]: scope, ...data })
            .returning('*')
          // SQLite does not support RETURNING — fall back to re-reading
          return (inserted[0] ?? (await this.get(scope))) as T
        } catch (e) {
          handleDbError(e)
        }
      }
    }

    // Global mode — single row in the table
    const existing = await this.knex(this.table).first<T | undefined>()

    if (existing) {
      const id = (existing as Record<string, unknown>)['id'] as string
      try {
        const updated = await this.knex(this.table)
          .where('id', id)
          .update(data)
          .returning('*')
        return (updated[0] ?? (await this.get())) as T
      } catch (e) {
        handleDbError(e)
      }
    } else {
      try {
        const inserted = await this.knex(this.table)
          .insert({ id: idGenerator(), ...data })
          .returning('*')
        return (inserted[0] ?? (await this.get())) as T
      } catch (e) {
        handleDbError(e)
      }
    }
  }
}
